'use client';

import React, { useEffect, useRef, useState } from 'react';
import SliderWithTabs from './slider_with_tabs';
import Button from '@/components/ui/button';
import Image from 'next/image';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import Carousel from 'react-bootstrap/esm/Carousel';
import { TabBannerGroup } from '@/types/homepage/homepageTypes';
import { Swiper as SwiperCore } from 'swiper';
import { usePostHogEvents } from '@/hooks/usePostHogEvents';
import { getThemeClass } from '@/helpers/theme';
import { cn } from '@/lib/utils';

const SlideComponent = ({ slideData }: { slideData: TabBannerGroup }) => {
  const [index, setIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [direction, setDirection] = useState<'rtl' | 'ltr'>('rtl');
  const startXRef = useRef<number | null>(null);
  const endXRef = useRef<number | null>(null);
  const swiperRef = useRef<SwiperCore | null>(null);
  const { sendButtonClickEvent } = usePostHogEvents();

  const handleSelect = (selectedIndex: number) => {
    console.log(selectedIndex);
    setIndex(selectedIndex);
    setDirection(selectedIndex > index ? 'rtl' : 'ltr');
  };

  useEffect(() => {
    if (swiperRef.current && isMobile) {
      const currentIndex = swiperRef.current.realIndex;
      if (currentIndex !== index) {
        swiperRef.current.slideToLoop(index); // Use slideToLoop to handle loop mode
      }
    }
  }, [index, isMobile]);

  const handleStart = (event: React.PointerEvent<HTMLDivElement>) => {
    startXRef.current = event.clientX;
    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleEnd);
  };

  const handleMove = (event: PointerEvent) => {
    endXRef.current = event.clientX;
  };

  const checkIfMobile = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  const handleEnd = () => {
    if (startXRef.current !== null && endXRef.current !== null) {
      const deltaX = endXRef.current - startXRef.current;
      const threshold = 50;

      if (deltaX > threshold && index > 0) {
        setIndex(index - 1);
        setDirection('ltr');
      } else if (deltaX < -threshold && index < slideData?.tabs?.length - 1) {
        setIndex(index + 1);
        setDirection('rtl');
      }
    }
    startXRef.current = null;
    endXRef.current = null;
    document.removeEventListener('pointermove', handleMove);
    document.removeEventListener('pointerup', handleEnd);
  };

  useEffect(() => {
    checkIfMobile(); // Initial check
    window.addEventListener('resize', checkIfMobile); // Listen for resize events
    return () => {
      window.removeEventListener('resize', checkIfMobile); // Clean up on component unmount
    };
  }, []);

  const theme = slideData?.tabs[index]?.theme || 'dark';

  return (
    <section
      className={`slidesWithTabs  select-none relative h-[600px] ${getThemeClass(theme, 'background')} md:h-section md:rounded-xl overflow-hidden max-w-desktop md:mx-auto `}
      {...(!isMobile && { onPointerDown: handleStart })}
    >
      <SliderWithTabs
        heading={slideData?.heading}
        slides={slideData?.tabs}
        slideTo={handleSelect}
        activeTab={index}
        isMobile={isMobile}
      >
        {!isMobile ? (
          <Carousel
            activeIndex={isMobile ? undefined : index}
            onSelect={handleSelect}
            interval={null}
            touch={true}
            className={`h-full ${getThemeClass(theme, 'background')}`}
          >
            {slideData?.tabs?.map((item, i) => (
              <Carousel.Item
                key={i}
                className={`h-full ${getThemeClass(theme, 'background')}`}
              >
                <div
                  className={`h-full w-full flex flex-col justify-end items-center relative`}
                >
                  {item?.image && (
                    <Image
                      src={`${process.env.NEXT_PUBLIC_BASE_URL}/assets/${item?.image.id}`}
                      alt={item?.title}
                      title={item?.title}
                      width={600}
                      height={600}
                      sizes="(max-width: 768px) 200px, 350px"
                      loading="lazy"
                      className="w-auto md:h-[350px] h-40 px-8 md:px-0 z-10 pointer-events-none"
                      onDragStart={(e) => e.preventDefault()}
                    />
                  )}
                  {item?.bg_image && (
                    <Image
                      src={`${process.env.NEXT_PUBLIC_BASE_URL}/assets/${item?.bg_image}`}
                      alt={item?.title}
                      title={item?.title}
                      fill
                      loading="lazy"
                      sizes="(max-width: 1200px) 100vw, 1280px"
                      className="w-full h-full object-cover"
                      onDragStart={(e) => e.preventDefault()}
                    />
                  )}
                  <div
                    className={`w-full z-10 px-2 text-center flex flex-col gap-3 lg:gap-3 items-center pt-3 pb-0 md:pb-16 transition-transform slider-content ${direction === 'rtl' ? 'slidein-rtl' : 'slidein-ltr'}`}
                  >
                    <h1
                      className={`font-bold ${getThemeClass(theme, 'heading')} text-2xl leading-[32px] ${slideData?.tabs?.length === 1 ? 'md:text-[32px] md:leading-[48px]' : 'md:text-[24px] md:leading-[32px]'}`}
                    >
                      {item?.title}
                    </h1>
                    <p
                      className={`md:text-[18px] text-[16px] font-proxima_font ${getThemeClass(theme, 'text')}`}
                    >
                      {item?.sub_title}
                    </p>
                    <Button
                      onClick={() => {
                        sendButtonClickEvent(item?.cta_text, item?.title);
                      }}
                      label={(item?.cta_text + '>') as string}
                      color={item?.cta_color ?? 'white'}
                      to={item?.cta_url}
                      className="text-[14px] font-semibold mt-4 font-proxima_font"
                    />
                  </div>
                </div>
              </Carousel.Item>
            ))}
          </Carousel>
        ) : (
          <Swiper
            pagination={false}
            onSlideChange={(e) => {
              if (index !== e.realIndex) {
                setIndex(e.realIndex); // Only update when necessary
              }
            }}
            effect="slide"
            className={`h-full ${getThemeClass(theme, 'background')}`}
            onSwiper={(swiper) => (swiperRef.current = swiper)} // Store Swiper instance
            loop={true}
          >
            {slideData?.tabs?.map((item, i) => (
              <SwiperSlide
                key={i}
                className={`h-full ${getThemeClass(theme, 'background')}`}
              >
                <div className="h-full w-full flex flex-col justify-end items-center cursor-grab relative">
                  {item?.image && (
                    <Image
                      src={`${process.env.NEXT_PUBLIC_BASE_URL}/assets/${item?.image.id}`}
                      alt={item?.title}
                      title={item?.title}
                      width={600}
                      height={600}
                      sizes="(max-width: 768px) 300px, 450px"
                      loading="lazy"
                      className="max-w-sm md:h-auto h-64 object-contain z-10 px-8 md:px-0 "
                    />
                  )}
                  {item?.bg_image && (
                    <>
                      {item?.mobile_bg_image && (
                        <Image
                          src={`${process.env.NEXT_PUBLIC_BASE_URL}/assets/${item.mobile_bg_image}`}
                          alt={item?.title}
                          title={item?.title}
                          fill
                          loading="lazy"
                          sizes="(max-width: 639px) 100vw, 0px"
                          className="w-full h-full object-cover block sm:hidden"
                          onDragStart={(e) => e.preventDefault()}
                        />
                      )}
                      <Image
                        src={`${process.env.NEXT_PUBLIC_BASE_URL}/assets/${item?.bg_image}`}
                        alt={item?.title}
                        title={item?.title}
                        fill
                        loading="lazy"
                        sizes={
                          item?.mobile_bg_image
                            ? '(max-width: 639px) 0px, (max-width: 1200px) 100vw, 1280px'
                            : '(max-width: 1200px) 100vw, 1280px'
                        }
                        className={cn(
                          'w-full h-full object-cover',
                          item?.mobile_bg_image ? 'hidden sm:block' : 'block'
                        )}
                        onDragStart={(e) => e.preventDefault()}
                      />
                    </>
                  )}
                  <div
                    className={`w-full z-10 px-6 justify-self-end text-center flex flex-col gap-3 lg:gap-5 items-center pb-16 md:pb-10 `}
                  >
                    <h1
                      className={`font-bold ${getThemeClass(theme, 'heading')} text-2xl leading-[32px] ${slideData?.tabs?.length === 1 ? 'md:text-[32px] md:leading-[48px]' : 'md:text-[24px] md:leading-[32px]'}`}
                    >
                      {item?.title}
                    </h1>
                    <p
                      className={`text-[16px] md:text-base lg:text-[18px] font-proxima_font ${getThemeClass(theme, 'text')}`}
                    >
                      {item?.sub_title}
                    </p>
                    <Button
                      onClick={() => {
                        sendButtonClickEvent(item?.cta_text, item?.title);
                      }}
                      label={(item?.cta_text + '>') as string}
                      color={item?.cta_color ?? 'white'}
                      to={item?.cta_url}
                    />
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        )}
      </SliderWithTabs>
    </section>
  );
};

export default SlideComponent;
